import { Button, Switch, toast, Typography } from "@suraasa/placebo-ui"
import { Theme, useMediaQuery } from "@suraasa/placebo-ui-legacy"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { SkillEvidence } from "api/resources/profile/types"
import { APIError } from "api/utils"
import Monitor from "assets/Fallback/monitor.svg"
import Fallback from "components/Fallback"
import ItemList from "components/ItemList"
import ItemRow from "components/ItemRow"
import { useState } from "react"
import { useTheme } from "react-jss"
import { handleErrors } from "utils/helpers"
import useArray from "utils/hooks/useArray"

import styles from "./EvidenceSetting.module.css"

const EvidenceSetting = ({
  skillEvidences = [],
  onClose,
  courseSlug,
}: {
  onClose?: () => void
  courseSlug: string
  skillEvidences: SkillEvidence[]
}) => {
  const queryClient = useQueryClient()
  const theme = useTheme<Theme>()
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"))

  const evidences = skillEvidences.find(item => item.slug === courseSlug)
  const [evidenceList, setEvidenceList] = useState<SkillEvidence | undefined>(
    evidences
  )

  const updatedList = useArray<{
    visibleOnProfile: boolean
    assignment: string
  }>([])

  const updateVisibility = (uuid: string, newVisibility: boolean) => {
    setEvidenceList(prevState => {
      if (!prevState) {
        return prevState
      }
      return {
        ...prevState,
        evidences: prevState.evidences.map(evidence => {
          if (evidence.uuid === uuid) {
            return {
              ...evidence,
              visibility: newVisibility,
            }
          }
          return evidence
        }),
      }
    })
  }

  const { mutate: toggleVisibility, isLoading } = useMutation({
    mutationFn: () =>
      api.profile.skillEvidences.toggleVisibility({
        data: {
          assignments: updatedList.array,
        },
      }),
    onSuccess: () => {
      toast.success("Preferences saved")
      queryClient.invalidateQueries(
        queries.profile.skillEvidenceList().queryKey
      )
      onClose?.()
    },
    onError: err => {
      if (err instanceof APIError) {
        handleErrors(err)
      }
    },
  })

  if (!evidenceList) {
    return (
      <Fallback
        className="my-4"
        data={{ image: Monitor, title: "There are no evidences available" }}
      />
    )
  }

  return (
    <div className="relative flex flex-col">
      <div className="mb-2 grow">
        <div className="mb-2 rounded-2xl border border-onSurface-200 p-2">
          <Typography className="mb-0.5" variant="title3">
            Show evidences on your skill portfolio
          </Typography>
          <Typography>
            Evidences will be visible to everyone who has access to your Suraasa
            profile or skill portfolio.
          </Typography>
        </div>
        <ItemList className="mb-2">
          {evidenceList.evidences.map((item, index) => (
            <ItemRow
              key={index}
              className={styles.rowContainer}
              infoSlot={
                <div className="py-1">
                  <Typography variant="strong">{item.title}</Typography>
                </div>
              }
              actionSlot={() => {
                return (
                  <Switch
                    size="xs"
                    checked={item.visibility}
                    onCheckedChange={e => {
                      updateVisibility(item.uuid, e)
                      const hasUpdated = updatedList.array.findIndex(
                        i => i.assignment === item.uuid
                      )
                      if (hasUpdated < 0) {
                        updatedList.push({
                          visibleOnProfile: e,
                          assignment: item.uuid,
                        })
                      } else {
                        updatedList.remove(hasUpdated)
                      }
                    }}
                  />
                )
              }}
            />
          ))}
        </ItemList>
      </div>
      {isXsDown ? (
        <Button
          color="black"
          onClick={() => {
            toggleVisibility()
          }}
          className="w-full"
          loading={isLoading}
        >
          Save Changes
        </Button>
      ) : (
        <div className="mx-auto flex w-full flex-col items-center justify-center rounded-2xl bg-onSurface-900 p-3 sm:flex-row sm:justify-between">
          <Typography className="mb-2 text-white sm:mb-0">
            Done making changes?
          </Typography>
          <Button
            size="sm"
            onClick={() => {
              toggleVisibility()
            }}
            loading={isLoading}
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  )
}

export default EvidenceSetting
